import {useEffect} from "react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {type IModalOverlayVariant, Modal} from "@pg-design/modal-module";
import {getPhoneDataFromString} from "@pg-mono/string-utils";

import {ApplicationMultiLead} from "../../../application/components/multi_lead/ApplicationMultiLead";
import {ApplicationMultiLeadResponsiveModal} from "../../../application/components/multi_lead/ApplicationMultiLeadResponsiveModal";
import {leadModalZindex} from "../../../application/constants/lead/lead_modal_zindex";
import {useMultiLeadRecommendations} from "../../../application/hooks/multi_lead/use_application_multi_lead_recommendations";
import {IApplicationFormValues} from "../../../application/types/form/IApplicationFormValues";
import {IApplicationData} from "../../../application/types/IApplicationData";
import {ApplicationMultiLeadRecommendationType} from "../../../application/types/multi_lead/ApplicationMultiLeadRecommendationType";
import {ApplicationMultiLeadTracking} from "../../../application/types/multi_lead/ApplicationMultiLeadTracking";
import {IApplicationMultiLeadProps} from "../../../application/types/multi_lead/IApplicationMultiLeadProps";
import {IApplicationMultiLeadSummaryStepState} from "../../../application/types/multi_lead/IApplicationMultiLeadSummaryStepState";
import {ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {useAppSelector} from "../../../store/hooks";
import {AmplitudeAuthPlacement} from "../../../tracking/types/AmplitudeAuthPlacement";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useGetHomeMatchConfigurationQuery} from "../../../user/api/patch_home_match_configuration";
import {HomeMatchBasicPreferencesFlowSource} from "../../../user/types/HomeMatchBasicPreferencesFlowTypes";
import {gtmRegistrationMultileadClick, gtmRegistrationMultileadClose, gtmRegistrationMultileadView} from "../../tracking/registration_multilead_tracking";

interface IProps {
    onComplete: (isHomeMatchOn: boolean, source: HomeMatchBasicPreferencesFlowSource, sentPropertyIds: number[]) => void;
    onClose: (isHomeMatchOn: boolean) => void;
    originalApplicationResponse?: IApplicationData | null;
    overlayVariant?: IModalOverlayVariant;
    variant?: "modal" | "responsiveModal";
    amplitudeAuthPlacement?: AmplitudeAuthPlacement | null;
}

export const RegistrationMultilead = ({
    onComplete,
    onClose,
    originalApplicationResponse = null,
    overlayVariant,
    variant = "modal",
    amplitudeAuthPlacement
}: IProps) => {
    const {data: userProfile, isLoading: isProfileLoading} = useGetUserProfileQuery();
    const {data: homeMatchConfig} = useGetHomeMatchConfigurationQuery();
    const flowState = useAppSelector((state) => state.registrationFlow);
    const {areRecommendationsLoading, areRecommendationsEmpty, recommendedProperties} = useMultiLeadRecommendations({
        propertyId: flowState.propertyId,
        offerId: flowState.offerId
    });
    // When the multilead follows a sent price-reveal lead, the original offer is shown as an
    // already-sent box instead of a selectable one (SRV-560), so it does not count as an offer here
    const isAfterSentLead = Boolean(originalApplicationResponse);
    // A compass offer takes no leads, so it is never offered for selection
    const showOriginalOffer = !isAfterSentLead && !flowState.useConsultantFlow;
    const totalOffers = recommendedProperties.length + (showOriginalOffer ? 1 : 0);

    useEffect(() => {
        if (!areRecommendationsLoading && areRecommendationsEmpty) {
            handleClose();
        }
    }, [areRecommendationsLoading, areRecommendationsEmpty]);

    const originalApplicationFormValues: IApplicationFormValues | null = userProfile
        ? {
              name: userProfile.name ?? "",
              email: userProfile.email ?? "",
              phone: getPhoneDataFromString(userProfile.phone ?? ""),
              marketing_email: userProfile.email_marketing_consent ?? false,
              marketing_sms: userProfile.phone_marketing_consent ?? false
          }
        : null;

    const handleComplete = (payload: IApplicationMultiLeadSummaryStepState) => {
        const sentPropertyIds = (payload.multiLeadSummaryItems ?? []).map(({id}) => id).filter((id): id is number => typeof id === "number");

        onComplete(homeMatchConfig?.is_active ?? false, "afterMultilead", sentPropertyIds);
    };

    const handleClose = () => {
        onClose(homeMatchConfig?.is_active ?? false);
    };

    const registrationTracking: ApplicationMultiLeadTracking = {
        onView: gtmRegistrationMultileadView,
        onClose: gtmRegistrationMultileadClose,
        onSend: gtmRegistrationMultileadClick
    };

    const handleModalClose = () => {
        gtmRegistrationMultileadClose(totalOffers);
        handleClose();
    };

    const isLoading = isProfileLoading || areRecommendationsLoading;
    const multiLeadProps: IApplicationMultiLeadProps | null =
        originalApplicationFormValues && flowState.propertyId && flowState.offerId && flowState.vendorId
            ? {
                  originalApplicationResponse,
                  sourceSection: ApplicationSourceSection.POPUP_MODAL,
                  recommendationType: ApplicationMultiLeadRecommendationType.PROPERTY,
                  originalApplicationFormValues,
                  recommendationsWithPriceFilter: true,
                  showOriginalOffer,
                  handleModalClose: handleClose,
                  handleSetMultiLeadSummaryStep: handleComplete,
                  customGtmTracking: registrationTracking,
                  customSourceSection: "post_price_reveal_multilead",
                  disableDefaultSelection: true,
                  amplitudeAuthPlacement
              }
            : null;

    if (variant === "responsiveModal") {
        return <ApplicationMultiLeadResponsiveModal isLoading={isLoading} multiLeadProps={multiLeadProps} onClose={handleModalClose} />;
    }

    const content = (() => {
        if (isLoading) {
            return (
                <div className={loaderWrapperStyle}>
                    <Loader size="lg" fullWidth />
                </div>
            );
        }

        if (!multiLeadProps) {
            return null;
        }

        return <ApplicationMultiLead {...multiLeadProps} />;
    })();

    return (
        <Modal isOpen onModalClose={handleModalClose} zIndex={leadModalZindex} showCloseButton={false} fullScreen="xs" overlayVariant={overlayVariant}>
            <div className={contentWrapperStyle}>{content}</div>
        </Modal>
    );
};

const contentWrapperStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    overflow: "hidden",
    height: "100%",
    width: "100%",
    minWidth: "28rem"
});

const loaderWrapperStyle = css({
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "24rem",

    md: {
        width: "560px",
        height: "auto",
        minHeight: "28rem"
    }
});
import {useEffect} from "react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {type IModalOverlayVariant, Modal} from "@pg-design/modal-module";
import {getPhoneDataFromString} from "@pg-mono/string-utils";

import {ApplicationMultiLead} from "../../../application/components/multi_lead/ApplicationMultiLead";
import {ApplicationMultiLeadResponsiveModal} from "../../../application/components/multi_lead/ApplicationMultiLeadResponsiveModal";
import {leadModalZindex} from "../../../application/constants/lead/lead_modal_zindex";
import {useMultiLeadRecommendations} from "../../../application/hooks/multi_lead/use_application_multi_lead_recommendations";
import {IApplicationFormValues} from "../../../application/types/form/IApplicationFormValues";
import {IApplicationData} from "../../../application/types/IApplicationData";
import {ApplicationMultiLeadRecommendationType} from "../../../application/types/multi_lead/ApplicationMultiLeadRecommendationType";
import {ApplicationMultiLeadTracking} from "../../../application/types/multi_lead/ApplicationMultiLeadTracking";
import {IApplicationMultiLeadProps} from "../../../application/types/multi_lead/IApplicationMultiLeadProps";
import {IApplicationMultiLeadSummaryStepState} from "../../../application/types/multi_lead/IApplicationMultiLeadSummaryStepState";
import {ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {useAppSelector} from "../../../store/hooks";
import {AmplitudeAuthPlacement} from "../../../tracking/types/AmplitudeAuthPlacement";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useGetHomeMatchConfigurationQuery} from "../../../user/api/patch_home_match_configuration";
import {HomeMatchBasicPreferencesFlowSource} from "../../../user/types/HomeMatchBasicPreferencesFlowTypes";
import {gtmRegistrationMultileadClick, gtmRegistrationMultileadClose, gtmRegistrationMultileadView} from "../../tracking/registration_multilead_tracking";

interface IProps {
    onComplete: (isHomeMatchOn: boolean, source: HomeMatchBasicPreferencesFlowSource, sentPropertyIds: number[]) => void;
    onClose: (isHomeMatchOn: boolean) => void;
    originalApplicationResponse?: IApplicationData | null;
    overlayVariant?: IModalOverlayVariant;
    variant?: "modal" | "responsiveModal";
    amplitudeAuthPlacement?: AmplitudeAuthPlacement | null;
}

export const RegistrationMultilead = ({
    onComplete,
    onClose,
    originalApplicationResponse = null,
    overlayVariant,
    variant = "modal",
    amplitudeAuthPlacement
}: IProps) => {
    const {data: userProfile, isLoading: isProfileLoading} = useGetUserProfileQuery();
    const {data: homeMatchConfig} = useGetHomeMatchConfigurationQuery();
    const flowState = useAppSelector((state) => state.registrationFlow);
    const {areRecommendationsLoading, areRecommendationsEmpty, recommendedProperties} = useMultiLeadRecommendations({
        propertyId: flowState.propertyId,
        offerId: flowState.offerId
    });
    // When the multilead follows a sent price-reveal lead, the original offer is shown as an
    // already-sent box instead of a selectable one (SRV-560), so it does not count as an offer here
    const isAfterSentLead = Boolean(originalApplicationResponse);
    // A compass offer takes no leads, so it is never offered for selection
    const showOriginalOffer = !isAfterSentLead && !flowState.useConsultantFlow;
    const totalOffers = recommendedProperties.length + (showOriginalOffer ? 1 : 0);

    useEffect(() => {
        if (!areRecommendationsLoading && areRecommendationsEmpty) {
            handleClose();
        }
    }, [areRecommendationsLoading, areRecommendationsEmpty]);

    const originalApplicationFormValues: IApplicationFormValues | null = userProfile
        ? {
              name: userProfile.name ?? "",
              email: userProfile.email ?? "",
              phone: getPhoneDataFromString(userProfile.phone ?? ""),
              marketing_email: userProfile.email_marketing_consent ?? false,
              marketing_sms: userProfile.phone_marketing_consent ?? false
          }
        : null;

    const handleComplete = (payload: IApplicationMultiLeadSummaryStepState) => {
        const sentPropertyIds = (payload.multiLeadSummaryItems ?? []).map(({id}) => id).filter((id): id is number => typeof id === "number");

        onComplete(homeMatchConfig?.is_active ?? false, "afterMultilead", sentPropertyIds);
    };

    const handleClose = () => {
        onClose(homeMatchConfig?.is_active ?? false);
    };

    const registrationTracking: ApplicationMultiLeadTracking = {
        onView: gtmRegistrationMultileadView,
        onClose: gtmRegistrationMultileadClose,
        onSend: gtmRegistrationMultileadClick
    };

    const handleModalClose = () => {
        gtmRegistrationMultileadClose(totalOffers);
        handleClose();
    };

    const isLoading = isProfileLoading || areRecommendationsLoading;
    const multiLeadProps: IApplicationMultiLeadProps | null =
        originalApplicationFormValues && flowState.propertyId && flowState.offerId && flowState.vendorId
            ? {
                  originalApplicationResponse,
                  sourceSection: ApplicationSourceSection.POPUP_MODAL,
                  recommendationType: ApplicationMultiLeadRecommendationType.PROPERTY,
                  originalApplicationFormValues,
                  recommendationsWithPriceFilter: true,
                  showOriginalOffer,
                  handleModalClose: handleClose,
                  handleSetMultiLeadSummaryStep: handleComplete,
                  customGtmTracking: registrationTracking,
                  customSourceSection: "post_price_reveal_multilead",
                  disableDefaultSelection: true,
                  amplitudeAuthPlacement
              }
            : null;

    if (variant === "responsiveModal") {
        return <ApplicationMultiLeadResponsiveModal isLoading={isLoading} multiLeadProps={multiLeadProps} onClose={handleModalClose} />;
    }

    const content = (() => {
        if (isLoading) {
            return (
                <div className={loaderWrapperStyle}>
                    <Loader size="lg" fullWidth />
                </div>
            );
        }

        if (!multiLeadProps) {
            return null;
        }

        return <ApplicationMultiLead {...multiLeadProps} />;
    })();

    return (
        <Modal isOpen onModalClose={handleModalClose} zIndex={leadModalZindex} showCloseButton={false} fullScreen="xs" overlayVariant={overlayVariant}>
            <div className={contentWrapperStyle}>{content}</div>
        </Modal>
    );
};

const contentWrapperStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    overflow: "hidden",
    height: "100%",
    width: "100%",
    minWidth: "28rem"
});

const loaderWrapperStyle = css({
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "24rem",

    md: {
        width: "560px",
        height: "auto",
        minHeight: "28rem"
    }
});
